import { useState } from 'react'
import { Card, Input, Person } from '../../../design/components'
import { matchingCharacters, type CampaignCharacter } from '../types'
import styles from './sections.module.css'

/**
 * The Faces of the city — design spec §5.4's "searchable roster of Faces with
 * portraits and their high concepts".
 *
 * The portraits and the search are here; **the high concepts are not**, and that is an
 * API gap rather than an omission. `CampaignCharacterDto` carries id, name, type, owner
 * and portrait — no aspects — and the only way to learn a face's High Concept today is
 * one full character-sheet read per face. Rendering the sheet's own line requires either
 * an aspects field on that DTO or a batch read; until one exists, each face links to its
 * sheet, where the High Concept is the first thing on the page.
 *
 * Filtering is client-side, unlike the character roster's: the cast arrives whole inside
 * the campaign's `characters` section, and a section is all-or-nothing — there is no
 * paged roster endpoint to push a `search` at.
 */
export function FacesCard({ roster }: { roster: readonly CampaignCharacter[] }) {
  const [search, setSearch] = useState('')
  const visible = matchingCharacters(roster, search)

  return (
    <Card variant="skills" label="Faces" hint={`${roster.length} in this city`}>
      {roster.length === 0 ? (
        <p className={styles.empty}>Nobody is in this campaign yet.</p>
      ) : (
        <>
          <Input
            label="Find a face"
            type="search"
            placeholder="Start typing…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          {visible.length === 0 && (
            <p className={styles.note}>Nobody here matches &ldquo;{search.trim()}&rdquo;.</p>
          )}

          {visible.map((character) => (
            <div key={character.characterId} className={styles.row}>
              <Person
                name={character.name}
                portraitUrl={character.portraitUrl}
                meta={[String(character.type), character.ownerName].filter(Boolean).join(' · ')}
                characterId={character.characterId}
              />
            </div>
          ))}
        </>
      )}
    </Card>
  )
}
